import React from 'react';
import SEO from '../components/SEO';
import { CheckCircle, Eye } from 'lucide-react';

export default function AccessibilityStatement() {
  return (
    <>
      <SEO
        title="Accessibility Statement | WCAG 2.1 AAA Compliance"
        description="TacticalSolutions.ai is committed to ensuring digital accessibility for people with disabilities. WCAG 2.1 AAA standards."
        keywords={['Accessibility', 'WCAG 2.1', 'Digital Inclusion', 'Screen Readers', 'Keyboard Navigation']}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center space-x-3 mb-8">
            <Eye className="w-10 h-10 text-teal-400" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Accessibility Statement
            </h1>
          </div>

          <div className="prose prose-invert prose-teal max-w-none">
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-6 mb-8">
              <p className="text-teal-300 mb-2">
                <strong>Last updated:</strong> January 2025
              </p>
              <p className="text-gray-300 mb-0">
                TacticalSolutions.ai is committed to ensuring digital accessibility for people with disabilities.
                We are continually improving the user experience for everyone and applying the relevant accessibility standards.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Conformance Status</h2>
            <p className="text-gray-300">
              The <a href="https://www.w3.org/WAI/WCAG21/quickref/" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300">Web Content Accessibility Guidelines (WCAG)</a> defines requirements for designers and developers to improve accessibility for people with disabilities. It defines three levels of conformance: Level A, Level AA, and Level AAA.
            </p>
            <p className="text-gray-300">
              TacticalSolutions.ai is fully conformant with WCAG 2.1 level AA and partially conformant with WCAG 2.1 level AAA.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Measures to Support Accessibility</h2>
            <p className="text-gray-300">
              TacticalSolutions.ai takes the following measures to ensure accessibility:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Include accessibility as part of our mission statement</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Integrate accessibility into our procurement practices</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Provide continual accessibility training for our staff</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Assign clear accessibility goals and responsibilities</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Employ formal accessibility quality assurance methods</span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Technical Specifications</h2>
            <p className="text-gray-300">
              Accessibility of TacticalSolutions.ai relies on the following technologies to work with the particular combination of web browser and any assistive technologies or plugins installed on your computer:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>HTML</li>
              <li>WAI-ARIA</li>
              <li>CSS</li>
              <li>JavaScript</li>
            </ul>
            <p className="text-gray-300">
              These technologies are relied upon for conformance with the accessibility standards used.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Feedback</h2>
            <p className="text-gray-300">
              We welcome your feedback on the accessibility of TacticalSolutions.ai. Please let us know if you encounter accessibility barriers:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>E-mail: accessibility@tacticalsolutions.ai</li>
              <li>Postal Address: London, United Kingdom</li>
            </ul>
            <p className="text-gray-300">
              We try to respond to feedback within 2 business days.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Compatibility with Browsers and Assistive Technology</h2>
            <p className="text-gray-300">
              TacticalSolutions.ai is designed to be compatible with the following assistive technologies:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>NVDA (Windows)</li>
              <li>JAWS (Windows)</li>
              <li>VoiceOver (macOS, iOS)</li>
              <li>TalkBack (Android)</li>
              <li>Dragon NaturallySpeaking</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Assessment Approach</h2>
            <p className="text-gray-300">
              TacticalSolutions.ai assessed the accessibility of this website by the following approaches:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>Self-evaluation</li>
              <li>External evaluation (pending third-party audit)</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Formal Complaints</h2>
            <p className="text-gray-300">
              If you are not satisfied with our response to your accessibility concern, you may contact the Equality and Human Rights Commission (EHRC):
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>EHRC Helpline: 0808 800 0082</li>
              <li>Website: <a href="https://www.equalityhumanrights.com" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300">equalityhumanrights.com</a></li>
            </ul>

            <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-6 mt-8">
              <p className="text-sm text-gray-400 mb-0">
                This statement was created on 27 January 2025 using the <a href="https://www.w3.org/WAI/planning/statements/" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300">W3C Accessibility Statement Generator Tool</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
